import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

import Maurice from "../assets/optimized/mauricecover.webp";
import Kim from "../assets/optimized/kimcover.webp";
import KM from "../assets/optimized/mjkim-new.webp";
import { Helmet } from "react-helmet";

export default function AboutUs() {
  const location = useLocation();
  return (
    <>
    <Helmet>
      <title>
        About Maurice & Kim Johnson | Pure Business Solutions
      </title>

      <meta
        name="description"
        content="Learn about Maurice and Kim Johnson, founders of Pure Business Solutions. Discover their passion for strengthening marriages, families, leaders, and communities through biblical coaching, leadership development, and faith-based relationship guidance."
      />

      <meta
        name="keywords"
        content="Maurice Johnson, Kim Johnson, Pure Business Solutions, relationship coach, marriage coach, Christian coaching, biblical counseling, leadership development, marriage ministry, faith-based coaching, Clarksville Tennessee"
      />

      <meta
        name="author"
        content="Pure Business Solutions"
      />

      <link
        rel="canonical"
        href="https://www.pbizs.com/about"
      />

      {/* Open Graph */}
      <meta
        property="og:title"
        content="About Maurice & Kim Johnson | Pure Business Solutions"
      />

      <meta
        property="og:description"
        content="Meet Maurice and Kim Johnson and learn how their ministry, coaching, and leadership experience helps individuals, couples, families, churches, and organizations flourish."
      />

      <meta
        property="og:image"
        content="https://www.pbizs.com/logo512.png"
      />

      <meta
        property="og:url"
        content="https://www.pbizs.com/about"
      />

      <meta
        property="og:type"
        content="profile"
      />

      {/* Twitter */}
      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content="About Maurice & Kim Johnson | Pure Business Solutions"
      />

      <meta
        name="twitter:description"
        content="Discover the story behind Pure Business Solutions and the mission of Maurice and Kim Johnson to strengthen marriages, relationships, families, and leaders through faith."
      />

      <meta
        name="twitter:image"
        content="https://www.pbizs.com/logo512.png"
      />
    </Helmet>
    <main className="bg-[#FFFDF9] text-[#2C2C2C] overflow-hidden">

      {/* ===================================================== */}
      {/* HERO */}
      {/* ===================================================== */}

      <section
        className="relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #FFFDF9 0%, #F8F3ED 50%, #EADCC8 100%)",
        }}
      >
        {/* Gold Glow */}
        <div className="absolute top-0 right-0 w-[550px] h-[550px] rounded-full bg-[#D4AF37]/20 blur-3xl" />

        {/* Pink Glow */}
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] rounded-full bg-[#D63384]/10 blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">

          <div className="grid lg:grid-cols-2 gap-20 items-center">

            {/* Left */}

            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: .8 }}
            >
              <p className="uppercase tracking-[4px] text-[#C8A46B] font-semibold">
                About Maurice & Kim
              </p>

              <h1 className="text-5xl lg:text-7xl font-bold mt-6 leading-tight">
                Helping People
                <span className="block text-[#D63384]">
                  Flourish
                </span>
                <span className="block text-[#C8A46B]">
                  Through Faith
                </span>
              </h1>

              <p className="mt-8 text-lg lg:text-xl text-gray-600 leading-relaxed max-w-xl">
                Maurice and Kim Johnson are passionate about helping
                individuals, couples, and families strengthen
                relationships, deepen their faith, and pursue lives
                filled with purpose through biblical wisdom and
                practical coaching.
              </p>

              <div className="flex flex-wrap gap-4 mt-10">

                <Link
                  to="/contact"
                  className="
                  bg-[#C8A46B]
                  hover:bg-[#B08C57]
                  text-white
                  px-8
                  py-4
                  rounded-full
                  font-semibold
                  shadow-lg
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  "
                >
                  Connect With Us
                </Link>

                <Link
                  to="/services"
                  state={{ background: location }}
                  className="
                  border-2
                  border-[#C8A46B]
                  px-8
                  py-4
                  rounded-full
                  font-semibold
                  hover:bg-[#FFF6EA]
                  transition-all
                  "
                >
                  Our Services
                </Link>

              </div>

            </motion.div>

            {/* Right */}

            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
              className="relative"
            >

              <div className="absolute -inset-5 bg-white/40 rounded-[40px] blur-xl" />

              <img
                src={KM}
                alt=""
                className="
                relative
                rounded-[36px]
                shadow-[0_25px_60px_rgba(0,0,0,.15)]
                w-full
                object-cover
                "
              />

            </motion.div>

          </div>

        </div>
      </section>

      {/* ===================================================== */}
      {/* OUR STORY */}
      {/* ===================================================== */}

      <section className="py-28 px-6 bg-white">

        <div className="max-w-5xl mx-auto text-center">

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .8 }}
          >

            <p className="uppercase tracking-[4px] text-[#C8A46B] font-semibold">
              Our Story
            </p>

            <h2 className="text-5xl font-bold mt-6">
              Serving With Purpose
            </h2>

            <div className="w-24 h-1 bg-[#C8A46B] rounded-full mx-auto my-8" />

            <p className="text-xl text-gray-600 leading-relaxed">
              Pure Business Solutions was founded from a shared calling—
              helping people flourish in every season of life. Maurice
              and Kim believe lasting transformation happens when faith,
              practical guidance, and authentic relationships come
              together.
            </p>

            <p className="text-lg text-gray-600 mt-8 leading-relaxed">
              Through coaching, retreats, conferences, leadership
              development, and biblical teaching, they have dedicated
              their lives to helping individuals, couples, families,
              churches, and organizations grow stronger in faith,
              communication, and purpose.
            </p>

          </motion.div>

        </div>

      </section>

      {/* ===================================================== */}
      {/* MAURICE */}
      {/* ===================================================== */}

      <section className="py-28 px-6 bg-[#F8F3ED]">

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">

          {/* Image */}

          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .8 }}
          >

            <img
              src={Maurice}
              alt=""
              className="
              rounded-[36px]
              shadow-2xl
              w-full
              object-cover
              "
            />

          </motion.div>

          {/* Content */}

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .8 }}
          >

            <p className="uppercase tracking-[4px] text-[#C8A46B] font-semibold">
              Meet Maurice
            </p>

            <h2 className="text-5xl font-bold mt-5">
              Maurice Johnson
            </h2>

            <div className="w-24 h-1 bg-[#C8A46B] rounded-full my-8" />

            <div className="flex flex-wrap gap-3 mb-8">

              {[
                "Ordained Elder",
                "Marriage Coach",
                "Speaker",
                "Army Veteran",
                "Mentor",
                "Leader",
              ].map((item) => (
                <span
                  key={item}
                  className="
                  bg-white
                  px-5
                  py-2
                  rounded-full
                  shadow-sm
                  font-medium
                  "
                >
                  {item}
                </span>
              ))}

            </div>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed">

              <p>
                Maurice Johnson is an Ordained Elder, visionary leader,
                and Certified Marriage, Relationship, and Empowerment
                Coach dedicated to helping individuals, couples, and
                families grow stronger through biblical wisdom and
                practical guidance.
              </p>

              <p>
                As Founder of Pure Business Solutions and Men Check
                Global Ministry, Maurice has devoted his life to
                equipping others with the tools needed to lead healthy
                relationships, strengthen families, and pursue God's
                purpose with confidence.
              </p>

              <p>
                After serving more than twelve years in the United States
                Army, Maurice continues supporting the military community
                while inspiring others through coaching, speaking, and
                leadership development.
              </p>

            </div>

          </motion.div>

        </div>

      </section>
            {/* ===================================================== */}
      {/* KIM */}
      {/* ===================================================== */}

      <section className="py-28 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">

          {/* Content */}

          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .8 }}
          >
            <p className="uppercase tracking-[4px] text-[#C8A46B] font-semibold">
              Meet Kim
            </p>

            <h2 className="text-5xl font-bold mt-5">
              Kim Johnson
            </h2>

            <div className="w-24 h-1 bg-[#C8A46B] rounded-full my-8" />

            <div className="flex flex-wrap gap-3 mb-8">

              {[
                "Published Author",
                "Ordained Elder",
                "Marriage Coach",
                "Teacher",
                "Administrator",
                "Event Strategist",
              ].map((item) => (
                <span
                  key={item}
                  className="
                  bg-[#F8F3ED]
                  px-5
                  py-2
                  rounded-full
                  shadow-sm
                  font-medium
                  "
                >
                  {item}
                </span>
              ))}

            </div>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed">

              <p>
                Kim Johnson is an Ordained Elder, Published Author,
                Teacher, Administrator, Event Strategist, and Certified
                Marriage, Relationship, and Empowerment Coach dedicated
                to helping others discover healing, hope, and purpose.
              </p>

              <p>
                As Co-Founder of Pure Business Solutions, Kim has
                invested years into serving individuals, couples,
                ministries, and organizations through coaching,
                leadership development, event management, and practical
                training.
              </p>

              <p>
                Drawing from her own testimony of overcoming trauma,
                abuse, and adversity, Kim encourages others to embrace
                healing, reclaim their identity, and walk boldly into
                the future God has prepared for them.
              </p>

            </div>

          </motion.div>

          {/* Image */}

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .8 }}
          >

          <div className="w-[110%]">
            <img
              src={Kim}
              alt="Kim Johnson"
              className="
                w-full
                rounded-[36px]
                shadow-2xl
                object-cover
              "
            />
          </div>

          </motion.div>

        </div>
      </section>

      {/* ===================================================== */}
      {/* TOGETHER */}
      {/* ===================================================== */}

      <section className="py-28 px-6 bg-[#F8F3ED]">

        <div className="max-w-5xl mx-auto text-center">

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .8 }}
          >

            <p className="uppercase tracking-[4px] text-[#C8A46B] font-semibold">
              Better Together
            </p>

            <h2 className="text-5xl font-bold mt-6">
              Together We Serve
            </h2>

            <div className="w-24 h-1 bg-[#C8A46B] rounded-full mx-auto my-8" />

            <p className="text-xl text-gray-600 leading-relaxed">
              Maurice and Kim believe that every person deserves hope,
              encouragement, and the opportunity to flourish. Together,
              they combine decades of ministry, leadership, coaching,
              and life experience to help individuals, couples,
              families, churches, and organizations experience lasting
              transformation.
            </p>

            <p className="text-lg text-gray-600 mt-8 leading-relaxed">
              Whether through coaching, conferences, retreats, speaking
              engagements, or leadership development, their mission is
              to equip others with practical tools and biblical
              principles that strengthen relationships, inspire
              confidence, and cultivate purposeful living.
            </p>

          </motion.div>

        </div>

      </section>

      {/* ===================================================== */}
      {/* MISSION & VISION */}
      {/* ===================================================== */}

      <section className="py-28 px-6 bg-white">

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10">

          {/* Mission */}

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="
            bg-[#FFFDF9]
            rounded-[32px]
            shadow-xl
            border-t-4
            border-[#C8A46B]
            p-10
            "
          >

            <p className="uppercase tracking-[3px] text-[#C8A46B] font-semibold">
              Our Mission
            </p>

            <h3 className="text-3xl font-bold mt-4">
              Empowering Lives Through Faith & Purpose
            </h3>

            <p className="text-gray-600 mt-6 leading-relaxed text-lg">
              Our mission is to equip individuals, couples, families,
              churches, and organizations with practical tools,
              biblical principles, and compassionate coaching that
              strengthen relationships, cultivate leadership, and
              inspire purposeful living.
            </p>

          </motion.div>

          {/* Vision */}

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: .15 }}
            className="
            bg-[#FFFDF9]
            rounded-[32px]
            shadow-xl
            border-t-4
            border-[#D63384]
            p-10
            "
          >

            <p className="uppercase tracking-[3px] text-[#D63384] font-semibold">
              Our Vision
            </p>

            <h3 className="text-3xl font-bold mt-4">
              Building Stronger Relationships & Communities
            </h3>

            <p className="text-gray-600 mt-6 leading-relaxed text-lg">
              We envision thriving marriages, healthy families,
              empowered leaders, and faith-filled communities where
              people are equipped to flourish personally,
              professionally, and spiritually through Christ-centered
              guidance.
            </p>

          </motion.div>

        </div>

      </section>

      {/* ===================================================== */}
      {/* CTA */}
      {/* ===================================================== */}

      <section className="py-28 px-6 bg-[#1F1F1F] text-white">

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >

          <p className="uppercase tracking-[4px] text-[#C8A46B] font-semibold">
            Let's Connect
          </p>

          <h2 className="text-5xl font-bold mt-6 leading-tight">
            Ready to Begin
            <span className="block text-[#C8A46B]">
              Your Journey?
            </span>
          </h2>

          <p className="mt-8 text-xl text-gray-300 leading-relaxed">
            Whether you're seeking marriage coaching, personal
            development, leadership training, or a speaker for your next
            event, we'd be honored to walk alongside you.
          </p>

          <Link
            to="/contact"
            className="
            inline-flex
            mt-12
            bg-[#C8A46B]
            hover:bg-[#B08C57]
            text-white
            px-10
            py-4
            rounded-full
            text-lg
            font-semibold
            transition-all
            duration-300
            hover:-translate-y-1
            shadow-xl
            "
          >
            Contact Us Today
          </Link>

        </motion.div>

      </section>

    </main>
    </>
  );
}